import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import InputBase from '@material-ui/core/InputBase';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
const EMPTY_GROUPS = [];

/**
 * Groups: { key, label, items }. Items: { key, label, icon?, disabled?, className?, searchText? }.
 * Icons are component types. Label and class callbacks customize presentation.
 * Use --sidebar-* CSS variables to customize colors without feature dependencies.
 */

const RMSMenuSidebar = ({
  groups = EMPTY_GROUPS,
  activeKey,
  onSelect,
  className,
  style,
  disabled = false,
  searchable = true,
  filterLabel = 'Filter menu',
  ariaLabel = 'Record sections',
  renderItemLabel = (item) => item.label,
  getItemClassName = (item) => item.className,
}) => {
  const [filter, setFilter] = useState('');
  // Track collapsed groups; everything starts expanded
  const [collapsed, setCollapsed] = useState({});
  const sidebarRef = useRef(null);
  const activeItemRef = useRef(null);

  const isFiltering = filter.trim().length > 0;

  const menu = groups;

  const visibleGroups = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return menu;
    return menu
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          String(item.searchText || item.label)
            .toLowerCase()
            .includes(term)
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [menu, filter]);

  const scrollActiveItemIntoView = useCallback(() => {
    const sidebar = sidebarRef.current;
    const item = activeItemRef.current;
    if (!sidebar || !item) return;
    const sidebarBounds = sidebar.getBoundingClientRect();
    const itemBounds = item.getBoundingClientRect();
    let offset = 0;
    if (itemBounds.bottom > sidebarBounds.bottom) {
      offset = itemBounds.bottom - sidebarBounds.bottom;
    } else if (itemBounds.top < sidebarBounds.top) {
      offset = itemBounds.top - sidebarBounds.top;
    }
    if (offset) sidebar.scrollTo({ top: sidebar.scrollTop + offset, behavior: 'smooth' });
  }, []);

  const toggleGroup = (groupKey) =>
    setCollapsed((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));

  useEffect(() => {
    const group = menu.find((entry) => entry.items.some((item) => item.key === activeKey));
    if (!group) return;
    setFilter('');
    setCollapsed((previous) =>
      previous[group.key] ? { ...previous, [group.key]: false } : previous
    );
  }, [activeKey, menu]);

  useLayoutEffect(() => {
    scrollActiveItemIntoView();
  }, [activeKey, visibleGroups, collapsed, scrollActiveItemIntoView]);

  return (
    <aside ref={sidebarRef} className={clsx('app-sidebar', className)} style={style}>
      {searchable && (
        <div className="app-sidebar__filter">
          <InputBase
            fullWidth
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            inputProps={{ 'aria-label': filterLabel }}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon className="app-sidebar__filter-icon" fontSize="small" />
              </InputAdornment>
            }
            classes={{ root: 'app-sidebar__filter-root', input: 'app-sidebar__filter-input' }}
          />
        </div>
      )}

      <nav className="app-sidebar__nav" aria-label={ariaLabel}>
        {visibleGroups.map((group) => {
          // While filtering, always show matching groups expanded
          const isOpen = isFiltering || !collapsed[group.key];

          return (
            <div key={group.key} className="app-sidebar__group">
              <ListItem
                button
                disableGutters
                className="app-sidebar__group-header"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={isOpen}>
                <ListItemText
                  primary={group.label}
                  classes={{ primary: 'app-sidebar__group-title' }}
                />
                {isOpen ? (
                  <ExpandLessIcon className="app-sidebar__chevron" fontSize="small" />
                ) : (
                  <ExpandMoreIcon className="app-sidebar__chevron" fontSize="small" />
                )}
              </ListItem>

              <Collapse
                in={isOpen}
                timeout="auto"
                unmountOnExit
                onEntered={scrollActiveItemIntoView}>
                <List disablePadding className="app-sidebar__list">
                  {group.items.map((item) => (
                    <ListItem
                      key={item.key}
                      ref={item.key === activeKey ? activeItemRef : null}
                      button
                      disabled={disabled || item.disabled}
                      aria-current={item.key === activeKey ? 'page' : undefined}
                      selected={item.key === activeKey}
                      onClick={() => {
                        if (!disabled && !item.disabled && onSelect) onSelect(item.key);
                      }}
                      className={clsx('app-sidebar__item', getItemClassName(item), {
                        'app-sidebar__item--active': item.key === activeKey,
                      })}>
                      {item.icon && (
                        <ListItemIcon className="app-sidebar__item-icon">
                          {<item.icon />}
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={renderItemLabel(item)}
                        classes={{ primary: 'app-sidebar__item-label' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default RMSMenuSidebar;
